/**
 * Translations for BeSafe Landing Page
 * Supports Spanish (es) and English (en)
 */

export type Language = 'es' | 'en';

export interface Translation {
  [key: string]: string | Translation;
}

export const translations: Record<Language, Translation> = {
  es: {
    nav: {
      services: 'Servicios',
      about: 'Nosotros',
      faq: 'Preguntas',
      contact: 'Contacto',
      login: 'Ingresar'
    },
    hero: {
      title: 'Arrendar Nunca fue tan fácil',
      subtitle: 'Somos la alternativa que facilita el acceso al mercado inmobiliario mediante análisis legal y financiero ágil. Gestiona todo tu proceso desde nuestra plataforma',
      uploadDocs: 'Iniciar Estudio',
      paymentCenter: 'Pagos',
      checkStatus: 'Mi Estudio'
    },
    about: {
      whoWeAre: {
        title: 'Nuestra razón de ser',
        content: 'Existimos para derribar las barreras del mercado inmobiliario. Combinamos la tecnología con el respaldo jurídico de Apriori Legal SAS para que más personas accedan a un techo de forma segura, ágil y transparente'
      },
      values: {
        title: 'Valores: De "Palabras" a "Garantías"',
        items: {
          0: {
            title: 'Respaldo Jurídico Total',
            content: 'Cada operación está protegida para cuidar los intereses de todas las partes. No solo somos una web, somos expertos legales.'
          },
          1: {
            title: 'Inclusión Financiera',
            content: 'Facilitamos el acceso al mercado para nuevos perfiles, promoviendo la equidad y nuevas oportunidades de vivienda.'
          },
          2: {
            title: 'Innovación Transparente',
            content: 'Utilizamos tecnología para que tus procesos sean claros, rápidos y sin letras chiquitas.'
          }
        }
      }
    },
    services: {
      title: 'Nuestros Servicios',
      items: {
        0: {
          title: 'Estudio de Crédito Digital',
          content: 'Análisis ágil para tu próximo arrendamiento. Evaluamos tu perfil legal y financiero en tiempo récord para que no pierdas el inmueble que quieres',
          button: 'Iniciar Estudio'
        },
        1: {
          title: 'Gestión de Pagos y Recaudo',
          content: 'Tus pagos al día, sin complicaciones. Una plataforma centralizada para el pago de tus cuotas mensuales con total transparencia',
          button: 'Ir a Pagos'
        },
        2: {
          title: 'Respaldo Integral Be Safe',
          content: 'Protección 360° para propietarios e inmobiliarias. Cubrimos el análisis legal, financiero y operativo para garantizar arrendamientos seguros',
          button: 'Ver Políticas'
        }
      },
      howItWorks: {
        title: 'Arrendar y pagar nunca fue tan sencillo',
        subtitle: 'Para Arrendatarios Nuevos (Estudio de Crédito)',
        steps: {
          0: 'Solicitud Online: Completa el formulario de estudio de crédito desde nuestra plataforma en minutos.',
          1: 'Carga Segura: Sube tus documentos de soporte con protocolos de alta seguridad.',
          2: 'Análisis Integral: Nuestro equipo experto realiza la evaluación legal y financiera.',
          3: 'Aprobación y Firma: Recibe tu respuesta y formaliza tu arrendamiento con total seguridad.'
        },
        currentClients: {
          subtitle: 'Para Clientes Actuales (Pago de Cuotas)',
          steps: {
            0: 'Ingreso: Accede a tu portal privado con tu usuario y contraseña.',
            1: 'Consulta: Visualiza tu estado de cuenta y cuotas pendientes de forma transparente.',
            2: 'Pago Ágil: Realiza el pago a través de nuestros canales digitales integrados.',
            3: 'Soporte: Descarga tus comprobantes y certificados al instante.'
          }
        },
        closing: 'Y así de FÁCIL arriendas tu inmueble con BESAFE'
      }
    },
    videoGallery: {
      title: 'Centro de Guía y Servicios',
      videos: {
        0: {
          title: 'Tu Estudio de Crédito en 3 Pasos',
          description: 'Explica cómo solicitar el estudio a través de la plataforma y la facilidad de cargar documentos de forma segura.'
        },
        1: {
          title: 'Pagos y Recaudo Sin Complicaciones',
          description: 'Muestra el portal de pagos, cómo ver las cuotas pendientes y los métodos de pago electrónicos disponibles.'
        },
        2: {
          title: 'El Respaldo de Be Safe',
          description: 'Explica el análisis legal, financiero y operativo realizado por Apriori Legal SAS para dar seguridad a todas las partes.'
        },
        3: {
          title: 'Inclusión: Oportunidades para Nuevos Perfiles',
          description: 'Describe cómo Be Safe permite el acceso al mercado inmobiliario a perfiles que no tienen garantías tradicionales.'
        }
      }
    },
    faq: {
      title: 'Preguntas Frecuentes',
      subtitle: 'Encuentra respuestas a las preguntas más comunes sobre nuestros servicios',
      items: {
        0: {
          question: '¿Qué es Be Safe?',
          answer: 'Be Safe es una solución creada por Apriori Legal S.A.S. que facilita el arrendamiento de inmuebles cuando el arrendatario no cumple con los requisitos de una aseguradora tradicional. Actuamos como respaldo dentro del proceso de arrendamiento, generando seguridad para propietarios, inmobiliarias y arrendatarios.'
        },
        1: {
          question: '¿Be Safe es una inmobiliaria?',
          answer: 'No. Be Safe no administra inmuebles. Trabajamos en alianza con inmobiliarias y propietarios, brindando respaldo dentro del proceso de arrendamiento.'
        },
        2: {
          question: '¿Quiénes pueden acceder al servicio?',
          answer: 'El servicio está pensado para personas o empresas que pueden tener dificultades para ser aprobadas por aseguradoras tradicionales, por ejemplo: Extranjeros sin historial crediticio en Colombia, Personas naturales sin historial financiero en el país, Pymes sin trayectoria crediticia sólida, Arrendatarios con reportes menores en centrales de riesgo.'
        },
        3: {
          question: '¿Cómo funciona el proceso?',
          answer: 'El proceso es simple: La inmobiliaria o el propietario remite la información del interesado. Se solicita documentación para el análisis. Se realiza un estudio de viabilidad financiera y jurídica. Si el perfil es aprobado, se formalizan los acuerdos. Se entrega un respaldo económico y se inicia el arrendamiento.'
        },
        4: {
          question: '¿Qué es el estudio de viabilidad?',
          answer: 'Es una evaluación que permite verificar la capacidad económica y los antecedentes del arrendatario para asegurar que pueda cumplir con las obligaciones del contrato de arrendamiento.'
        },
        5: {
          question: '¿Cuánto cuesta el estudio?',
          answer: 'Persona natural: $238.000 (IVA incluido). Persona jurídica: $357.000 (IVA incluido). El estudio se realiza normalmente en un plazo aproximado de 48 horas hábiles después de recibir los documentos y el comprobante de pago.'
        },
        6: {
          question: '¿Se debe entregar un depósito o respaldo?',
          answer: 'Sí. Una vez aprobado el estudio, el arrendatario debe entregar un respaldo económico equivalente a hasta tres cánones de arrendamiento, el cual sirve como garantía frente a posibles obligaciones derivadas del contrato.'
        },
        7: {
          question: '¿El dinero del respaldo se devuelve?',
          answer: 'Sí. El respaldo se reintegra al finalizar el contrato, una vez la inmobiliaria emita el paz y salvo. Si existen valores pendientes (como servicios, reparaciones o cánones), estos podrán descontarse del valor entregado como respaldo.'
        },
        8: {
          question: '¿Cuál es el costo del servicio?',
          answer: 'Be Safe cobra un servicio técnico mensual: 2.5% del canon de arrendamiento + IVA si el canon es igual o menor a $2.500.000. 2% del canon de arrendamiento + IVA si el canon es mayor a $2.500.000.'
        },
        9: {
          question: '¿Qué pasa si el arrendatario se atrasa en el pago?',
          answer: 'En caso de mora en el pago del canon o del servicio, se activa un procedimiento de gestión que puede incluir contacto con el cliente y medidas para la recuperación del inmueble conforme a los acuerdos firmados.'
        },
        10: {
          question: '¿Qué ventajas tiene usar Be Safe?',
          answer: 'Amplía las posibilidades de arrendamiento. Permite acceder a inmuebles sin depender de una aseguradora tradicional. Ofrece respaldo jurídico y procesos claros. Facilita el acceso a personas o empresas con perfiles no tradicionales en el mercado inmobiliario.'
        }
      }
    },
    contact: {
      title: 'Contáctanos',
      tagline: 'Respaldo integral para propietarios, inmobiliarias y arrendatarios, combinando análisis legal, financiero y operativo para garantizar procesos confiables',
      navigation: {
        title: 'Navegación',
        services: 'Servicios',
        about: 'Nosotros',
        faq: 'Preguntas',
        contact: 'Contacto'
      },
      actions: {
        title: 'Acciones Rápidas',
        requestStudy: 'Solicitar estudio de crédito',
        payQuota: 'Pagar mi cuota',
        uploadDocs: 'Cargar documentos'
      },
      info: {
        title: 'Información de Contacto',
        address: 'Calle 44 - 106 oficina 23, Centro Ejecutivo el Poblado, Medellín, Antioquia',
        whatsapp: '+57 312 4453260',
        email: 'apoyojuridico@apriorilegal.co',
        schedule: 'Lunes a Viernes: 8:00 AM - 5:00 PM'
      },
      allies: {
        title: 'Aliados'
      },
      footer: {
        copyright: '© 2026 BeSafe – A priori Legal – Todos los derechos reservados',
        privacy: 'Política de privacidad',
        dataProtection: 'Política de protección de datos',
        developer: 'Desarrollado por Jaibana IT'
      }
    },
    privacy: {
      title: 'Política de Privacidad',
      responsible: {
        title: 'Responsable del Tratamiento',
        content: 'APRIORI LEGAL SAS. con domicilio en Medellín, Colombia, es responsable del tratamiento de los datos personales. Según nuestras políticas de tratamiento de datos personales, los mecanismos a través de los cuales hacemos uso de éstos son seguros y confidenciales, pues contamos con los medios tecnológicos idóneos para asegurar que sean almacenados de manera tal que se impida el acceso indeseado por parte de terceras personas, y en ese mismo orden aseguramos la confidencialidad de los mismos.'
      },
      contact: {
        title: 'Cómo contactarnos',
        address: 'Dirección oficinas:',
        email: 'Correo electrónico:',
        phone: 'Teléfono:'
      },
      purpose: {
        title: 'Finalidades del Tratamiento',
        intro: 'Sus datos personales serán incluidos en una base de datos y serán utilizados para las siguientes finalidades:',
        item1: 'Lograr una eficiente comunicación relacionada con nuestros productos, servicios, ofertas, promociones, alianzas, estudios, concursos, contenidos, así como los de nuestras compañías vinculadas, y para facilitarle el acceso general a la información de éstos;',
        item2: 'Proveer nuestros servicios y productos;',
        item3: 'Informar sobre nuevos productos o servicios que estén relacionados con el o los contratado(s) o adquirido(s);',
        item4: 'Dar cumplimiento a obligaciones contraídas con nuestros clientes, proveedores, y empleados;',
        item5: 'Informar sobre cambios de nuestros productos o servicios;',
        item6: 'Evaluar la calidad del servicio, y',
        item7: 'Realizar estudios internos sobre hábitos de consumo.'
      },
      rights: {
        title: 'Sus Derechos',
        content: 'Se les informa a los titulares de información que pueden consultar el Manual Interno de Políticas y procedimientos de APRIORI LEGAL SAS, que contiene nuestras políticas para el tratamiento de la información recogida, así como los procedimientos de consulta y reclamación que le permitirán hacer efectivos sus derechos al acceso, consulta, rectificación, actualización y supresión de los datos, haciendo clic en el siguiente enlace:'
      },
      pdfLink: 'Descargar la Política de Tratamiento de Datos Personales (PDF)'
    },
    dataProtection: {
      title: 'Política de Protección de Datos',
      intro: 'APRIORI LEGAL SAS es una sociedad con domicilio principal en la ciudad de Medellín, Colombia, comprometida con el cumplimiento de las normas en materia de datos personales. En virtud de lo anterior, APRIORI LEGAL SAS se compromete a proteger la privacidad por el uso, recopilación y tratamiento de los datos personales de clientes, proveedores y empleados. APRIORI LEGAL SAS guarda estricta confidencialidad sobre los datos personales y garantiza el derecho que tienen los TITULARES de conocer, actualizar y rectificar la información contenida en nuestras bases de datos.',
      treatment: {
        title: 'Tratamiento de la Información',
        content: 'Los datos personales incorporados por APRIORI LEGAL SAS en las bases de datos y archivos, son tratados exclusivamente para las finalidades establecidas. Cada dato personal utilizado por APRIORI LEGAL SAS cuenta con la respectiva autorización. Una vez culminada la relación contractual, comercial o de cualquier otra naturaleza entre el Titular del dato personal y la Compañía, cesará el tratamiento de los datos personales, siempre que el Titular no tenga el deber legal o contractual de permanecer en la base de datos.'
      },
      duties: {
        title: 'Deberes de APRIORI LEGAL SAS como Responsable del Tratamiento',
        item1: 'Solicitar y conservar, en las condiciones previstas en la ley, copia de la respectiva autorización otorgada por el Titular.',
        item2: 'Informar debidamente al Titular sobre la finalidad de la recolección y los derechos que le asisten por virtud de la autorización otorgada.',
        item3: 'Conservar la información bajo las condiciones de seguridad necesarias para impedir su adulteración, pérdida, consulta, uso o acceso no autorizado o fraudulento.',
        item4: 'Garantizar que la información que se suministre al Encargado del Tratamiento sea veraz, completa, exacta, actualizada, comprobable y comprensible.',
        item5: 'Tramitar las consultas y reclamos formulados en los términos señalados en la ley.'
      },
      rights: {
        title: 'Derechos y Procedimientos',
        intro: 'APRIORI LEGAL SAS garantizará a los TITULARES de la información, el acceso, la rectificación y eliminación de los datos personales que hayan sido objeto de un tratamiento y a solicitar prueba de la autorización otorgada para su tratamiento.',
        access: {
          title: 'Derecho de Acceso',
          content: 'Toda persona tendrá derecho a conocer si sus datos personales han sido sometidos a un tratamiento por parte de APRIORI LEGAL SAS en los términos expresados en la Ley de Protección de Datos.'
        },
        rectification: {
          title: 'Derechos de Rectificación y Cancelación',
          content: 'Los TITULARES de la información tienen el derecho de comprobar frente al responsable del tratamiento de la información, la exactitud y veracidad del dato y solicitar su rectificación cuando los mismos resulten inexactos, estén incompletos o conlleven a error.'
        }
      },
      procedures: {
        title: 'Procedimientos de Consulta y Reclamos',
        content: 'Para ejercer estos derechos, el titular de la información podrá hacerlo a través de los medios habilitados por APRIORI LEGAL SAS:',
        phone: 'Teléfono:',
        email: 'Correo electrónico:',
        address: 'Dirección:'
      },
      validity: {
        title: 'Vigencia de la Política',
        content: 'Las bases de datos tendrán una vigencia igual al periodo en que se mantenga la finalidad o finalidades del tratamiento en cada base de datos, o el periodo de vigencia señalado en la Ley, los contratos, o formatos de captura respectiva.'
      },
      pdfLink: 'Descargar la Política de Tratamiento de Datos Personales (PDF)'
    },
    login: {
      branding: {
        title: 'Seguridad y confianza en cada arrendamiento',
        subtitle: 'Accede a tu cuenta para ingresar a la plataforma de gestión'
      },
      form: {
        title: 'Iniciar Sesión',
        subtitle: 'Ingresa tus credenciales para acceder',
        email: 'Correo Electrónico',
        password: 'Contraseña',
        remember: 'Recordarme',
        forgot: '¿Olvidaste tu contraseña?',
        submit: 'Iniciar Sesión',
        noAccount: '¿No tienes una cuenta?',
        register: 'Regístrate aquí',
        backHome: '← Volver al inicio'
      }
    },
    statusVerify: {
      title: 'Verificación de Estudio',
      subtitle: 'Ingresa tus credenciales para consultar el estado de tu estudio',
      userId: 'Identificador de Usuario',
      userIdHint: 'Ejemplo: USR-123456',
      password: 'Contraseña de Acceso',
      mfaCode: 'Código de Verificación',
      mfaHint: 'Ingresa el código de 6 dígitos enviado a tu correo',
      submit: 'Acceder',
      backHome: '← Volver al inicio',
      error: 'Credenciales incorrectas. Por favor, intenta de nuevo.',
      mfaModal: {
        title: 'Verificación de Seguridad',
        description: 'Hemos enviado un código de verificación a tu correo electrónico y número de teléfono registrado.',
        codeLabel: 'Código de Verificación',
        codeHint: 'Ingresa el código de 6 dígitos',
        verify: 'Verificar Código',
        resend: 'Reenviar Código',
        error: 'Código incorrecto. Por favor, intenta de nuevo.',
        success: '✓ Código verificado correctamente'
      }
    },
    statusDashboard: {
      title: 'Estado de tu Estudio',
      subtitle: 'Sigue el progreso de tu solicitud de arrendamiento',
      logout: 'Cerrar Sesión',
      backHome: '← Volver al inicio'
    },
    uploadDocs: {
      title: 'Gestión de Documentos',
      subtitle: 'Selecciona la opción que necesitas',
      backHome: '← Volver al inicio',
      option1: {
        title: 'Solicitar Estudio Nuevo',
        description: 'Primera vez en BeSafe. Regístrate y solicita tu estudio de arrendamiento.',
        button: 'Comenzar Registro'
      },
      option2: {
        title: 'Subir Comprobante de Pago',
        description: 'Usuario activo. Sube tu comprobante de pago mensual.',
        button: 'Subir Comprobante'
      },
      option3: {
        title: 'Subir Documentos para Estudio',
        description: 'Usuario registrado. Sube los documentos requeridos para tu estudio.',
        button: 'Subir Documentos'
      },
      newReg: {
        title: 'Solicitud de Estudio Nuevo',
        subtitle: 'Completa el formulario para iniciar tu proceso de registro',
        firstName: 'Nombre',
        lastName: 'Apellidos',
        phone: 'Teléfono de Contacto',
        email: 'Correo Electrónico',
        message: 'Mensaje de Solicitud',
        submit: 'Enviar Solicitud'
      },
      payment: {
        title: 'Subir Comprobante de Pago',
        subtitle: 'Verifica tu identidad y sube tu comprobante de pago mensual',
        userId: 'ID de Usuario',
        password: 'Contraseña',
        verify: 'Verificar Identidad',
        month: 'Mes',
        year: 'Año',
        file: 'Comprobante de Pago',
        dragDrop: 'Arrastra tu archivo aquí o haz clic para seleccionar',
        fileHint: 'PDF, JPG o PNG (máx. 10MB)',
        submit: 'Subir Comprobante'
      },
      study: {
        title: 'Subir Documentos para Estudio',
        subtitle: 'Verifica tu identidad y sube los documentos requeridos',
        userId: 'ID de Usuario',
        password: 'Contraseña',
        verify: 'Verificar Identidad',
        selectType: 'Selecciona el tipo de documento',
        types: {
          id: '📄 Documento de Identificación',
          financial: '💰 Estado Financiero',
          support: '📋 Soportes para Estudio'
        },
        uploadedDocs: 'Documentos Cargados',
        noDocs: 'No has cargado documentos aún',
        submit: 'Enviar Documentos'
      }
    },
    portal: {
      myContracts: 'Mis Contratos',
      welcome: 'Bienvenido',
      hero: 'Hola, {name}. Aquí ves el estado de todos tus contratos y trámites en un solo lugar.',
      activeContracts: 'Contratos activos',
      emptyFilter: 'No hay contratos con este filtro.',
      filters: {
        all: 'Todos',
        onTrack: 'Al día',
        attention: 'Atención'
      },
      attention: {
        overdueOne: '{count} contrato en mora',
        overdueMany: '{count} contratos en mora',
        soonOne: '{count} pago próximo',
        soonMany: '{count} pagos próximos',
        unpaidOne: '{count} estudio sin pagar',
        unpaidMany: '{count} estudios sin pagar'
      },
      detail: {
        tabSummary: 'Resumen',
        subSummary: 'Contrato, proceso y asesor',
        tabDocs: 'Documentos',
        subDocs: 'Sube y revisa tu expediente',
        tabPayments: 'Pagos',
        subPayments: 'Estado de cuenta e historial',
        pendTitle: 'Necesitan tu atención',
        allGoodTitle: 'Todo al día',
        payMoraTitle: 'Pagos en mora',
        payOkTitle: 'Estado de pagos',
        docsPendTitle: 'Documentos pendientes',
        docsPendValue: '{count} por subir',
        docsDoneTitle: 'Documentos',
        docsAllUploaded: 'Todos cargados',
        docsHelp: 'Sube los documentos requeridos para avanzar con tu estudio. Se renombran automáticamente.',
        requiredUploaded: 'documentos requeridos cargados'
      },
      logout: 'Cerrar sesión',
      privacyPolicy: 'Política de Tratamiento de Datos Personales',
      newStudy: 'Solicitar nuevo estudio',
      back: 'Volver',
      financialInfo: 'Información Financiera',
      assignedAdvisor: 'Asesor Asignado',
      processTimeline: 'Timeline del Proceso',
      personalDocs: 'Documentos Personales',
      personalInfo: 'Información Personal',
      personalInfoDesc: 'Contactos y ocupación registrados para el estudio',
      contacts: 'Contactos',
      occupations: 'Ocupaciones',
      addNew: 'Agregar nueva',
      removeSelected: 'Eliminar seleccionada',
      addContact: 'Agregar contacto',
      addOccupation: 'Agregar ocupación',
      cancel: 'Cancelar',
      save: 'Guardar',
      update: 'Actualizar',
      uploaded: 'Subido',
      noFiles: 'Sin archivos subidos.',
      pendingPayments: 'Pagos en mora',
      paymentsUpToDate: 'Pagos al día',
      paymentsUpToDateMsg: 'No tienes pagos pendientes. ¡Sigue así!',
      payments: 'Pagos',
      paymentsDesc: 'Paga tus cuotas y respaldos con Wompi',
      registerPayment: 'Registrar Pago',
      payWithWompi: 'Pagar con Wompi',
      paymentFullBalance: 'Saldo completo',
      paymentPartial: 'Pago parcial',
      contactAdvisor: 'Contactar Asesor',
      send: 'Enviar',
      noAdvisor: 'Asesor por asignar. Tu asesor será asignado pronto.',
      historyTitle: 'Historial de Pagos',
      noPayments: 'No hay pagos registrados.',
      noContracts: 'No se encontraron contratos asociados a tu cuenta.',
      requestStudy: 'Solicitar Estudio',
      loading: 'Cargando...',
      user: 'Usuario',
      retry: 'Reintentar',
      errorTitle: 'Error',
      authRequired: 'Debes iniciar sesión para acceder al portal',
      sessionExpired: 'Tu sesión ha expirado. Por favor inicia sesión de nuevo.',
      financial: {
        monthlyRent: 'Canon mensual',
        plusRent: 'Plus canon',
        backrest: 'Respaldo',
        commission: 'Comisión',
        totalCommission: 'Total comisión',
        duration: 'Duración',
        months: 'meses',
        property: 'Inmueble',
        startDate: 'Inicio',
        endDate: 'Fin',
        empty: 'No hay información financiera registrada.'
      },
      defaults: {
        warningHeader: 'Pagos en mora',
        warningBodyOne: 'Tienes <strong>{count} pago</strong> en mora por un total de <strong>{amount} COP</strong>.',
        warningBodyMany: 'Tienes <strong>{count} pagos</strong> en mora por un total de <strong>{amount} COP</strong>.',
        warningFooter: 'Por favor, regulariza tu situación lo antes posible para evitar acciones adicionales.'
      },
      advisor: {
        defaultName: 'Asesor',
        contactHelp: 'Puedes contactar a tu asesor para cualquier consulta sobre tu contrato.',
        messagePlaceholder: 'Escribe tu mensaje al asesor...',
        emptyMessage: 'Por favor escribe un mensaje',
        sending: 'Enviando...',
        sentSuccess: 'Mensaje enviado al asesor correctamente',
        loadingAdvisor: 'Cargando información del asesor...',
        emptyTitle: 'Asesor por asignar',
        emptySubtitle: 'Tu asesor será asignado pronto'
      },
      timeline: {
        empty: 'No hay información de timeline disponible.',
        title: 'Progreso del Contrato',
        completedAt: 'Completado',
        startedAt: 'Iniciado',
        itemNames: {
          'INICIO DEL PROCEDIMIENTO': 'Inicio del Procedimiento',
          'DOCUMENTACIÓN': 'Documentación',
          'ESTUDIO': 'Estudio',
          'GESTIÓN CONTRACTUAL': 'Gestión Contractual',
          'FIRMA DE CONTRATO DE ARRENDAMIENTO': 'Firma de Contrato de Arrendamiento',
          'MONITOREO': 'Monitoreo',
          'PAGO ESTUDIOS': 'Pago de Estudios',
          'DOCUMENTOS NECESARIOS': 'Documentos Necesarios',
          'CLIENTE APROBADO': 'Cliente Aprobado',
          'PAGO DEL RESPALDO': 'Pago del Respaldo',
          'PAGO COMISION': 'Pago de Comisión',
          'FIRMA DE ACUERDO': 'Firma de Acuerdo'
        },
        statuses: {
          'PENDIENTE': 'Pendiente',
          'EN PROCESO': 'En Proceso',
          'COMPLETADO': 'Completado',
          'APROBADO': 'Aprobado',
          'RECHAZADO': 'Rechazado',
          'NO APROBADO': 'No Aprobado',
          'CANCELADO': 'Cancelado',
          'ACTIVO': 'Activo',
          'FINALIZADO': 'Finalizado'
        }
      },
      documents: {
        types: 'Tipos',
        max: 'Máx',
        multipleFiles: 'Múltiples archivos',
        filesLabel: 'archivo',
        filesLabelPlural: 'archivos',
        update: 'Actualizar',
        empty: 'Sin archivos subidos.',
        other: 'Otros Documentos',
        listTitle: 'Documentos Subidos',
        listEmpty: 'No hay documentos subidos.',
        listEmptyHint: 'Los archivos que subas aparecerán aquí',
        loadingFiles: 'Cargando documentos...',
        filesCount: '{count} archivo(s)',
        colName: 'Archivo',
        colSize: 'Tamaño',
        colDate: 'Fecha',
        colStatus: 'Estado',
        statusUploaded: 'Subido',
        typeNotAllowed: 'Tipo no permitido para {label}. Aceptados: {types}',
        fileExceeds: 'Archivo excede {max}MB para {label}',
        singleFileOnly: 'Solo se permite un archivo para {label}',
        uploadedSingle: '{count} archivo subido',
        uploadedMany: '{count} archivos subidos',
        invalidType: 'Tipo de archivo no permitido: {ext}. Permitidos: {types}',
        noExtension: 'sin extensión',
        tooLarge: 'Archivo demasiado grande ({size}MB). Máximo: {max}MB',
        empty2: 'El archivo está vacío',
        uploadSuccessAlt: 'Archivo subido exitosamente',
        beforeUnload: '¿Está seguro? La subida en progreso se cancelará.',
        dropzonePrompt: 'Arrastra archivos aquí o',
        selectFiles: 'Seleccionar archivos',
        allowedTypes: 'Tipos permitidos',
        maxSize: 'Tamaño máximo',
        confirmUpload: 'Confirmar subida',
        cancel: 'Cancelar',
        uploading: 'Subiendo...',
        cancelUpload: 'Cancelar subida',
        refresh: 'Actualizar'
      },
      docsCategory: {
        studyTitle: 'Documentos para Estudio',
        studyDesc: 'Sube aquí tus documentos de identificación, comprobantes de ingresos, referencias, etc.'
      },
      docsCompletedMsg: 'La documentación está completa. En las próximas <strong>48 horas</strong> se realizará el estudio de tu solicitud.',
      contractsLoading: 'Cargando contratos...',
      contractLabels: {
        advisor: 'Asesor:',
        advisorRole: 'Asesor',
        created: 'Creado:',
        stepOf: 'de',
        rentCaption: 'Arriendo mensual',
        date: 'Fecha:',
        currentPhase: 'Fase actual:',
        unassigned: 'Sin asignar',
        viewDetails: 'Ver detalle →',
        property: 'Propiedad:',
        monthlyRent: 'Arriendo mensual:',
        startDate: 'Inicio:',
        endDate: 'Fin:',
        paymentStatus: 'Estado de pagos:',
        payStudy: 'Pagar estudio',
        unpaidStudiesTitle: 'Estudios sin pagar',
        unpaidStudiesHelp: 'Solicitudes que no completaron el pago del estudio. Continúa el pago para activarlas.',
        paymentAlDia: 'Al día',
        paymentProximo: 'Pago próximo',
        paymentMora: 'En mora',
        unknownAddress: 'Dirección no disponible',
        contractTitle: 'Contrato',
        propertyInfoTitle: 'Información de la Propiedad',
        address: 'Dirección:',
        period: 'Periodo:',
        type: 'Tipo:',
        rooms: 'Habitaciones:',
        documentsTitle: 'Documentos',
        paymentsMgmt: 'Gestión de Pagos',
        backHome: '← Volver al inicio'
      },
      paymentStatus: {
        upToDate: 'al día',
        overdue: 'en mora'
      },
      paymentInfo: {
        title: 'Información de Pagos',
        statusLabel: 'Estado: {status}',
        overdueCount: '({count} pago(s) atrasado(s))',
        monthlyService: 'Servicio (mensual):',
        deposit: 'Respaldo (depósito):',
        nextPayment: 'Próximo pago:',
        lastPayment: 'Último pago:',
        notSpecified: 'No especificado',
        payWithWompiTitle: 'Pagar con Wompi',
        payWithWompiNote: 'Será redirigido a la pasarela segura de Wompi'
      },
      paymentModal: {
        title: 'Registrar Pago',
        close: 'Cerrar',
        intro: 'Paga ahora con Wompi (tarjeta, PSE, Nequi, Bancolombia). El comprobante se genera automáticamente.',
        option: 'Opción de pago',
        full: 'Saldo completo',
        partial: 'Pago parcial',
        partialHint: 'Elige el monto',
        defaultsSummary: 'Mora acumulada: <strong>{defaults}</strong> + Servicio del mes: <strong>{service}</strong> = Total: <strong>{total}</strong>',
        monthSummary: 'Servicio del mes: <strong>{service}</strong>',
        respaldoSummary: 'Respaldo a pagar: <strong>{respaldo}</strong>',
        valueLabel: 'Valor ($)',
        conceptLabel: 'Concepto',
        conceptPlaceholder: 'Selecciona un concepto',
        observationLabel: 'Observación',
        observationPlaceholder: 'Observaciones adicionales (opcional)',
        valueError: 'El valor debe ser mayor a 0',
        conceptError: 'Concepto de pago inválido',
        observationLengthError: 'La observación no puede exceder {max} caracteres',
        observationCharsError: 'La observación contiene caracteres no permitidos',
        generating: 'Generando…',
        redirecting: 'Redirigiendo a Wompi…',
        previewError: 'Error al previsualizar comprobante',
        downloadError: 'Error al descargar comprobante',
        copySuccess: 'Referencia copiada',
        copyError: 'No se pudo copiar la referencia',
        manualSubmit: 'Enviar comprobante',
        respaldoNote: 'El respaldo se paga por transferencia. Adjunta el comprobante y un asesor lo validará.',
        dateLabel: 'Fecha del pago',
        methodLabel: 'Método de pago',
        methodPlaceholder: 'Selecciona el método',
        methodError: 'Selecciona el método de pago',
        comprobanteLabel: 'Comprobante (PDF o imagen)',
        comprobanteError: 'Adjunta el comprobante de la transferencia',
        uploading: 'Enviando…',
        uploadSuccess: 'Comprobante enviado. Un asesor lo validará.',
        uploadError: 'No se pudo enviar el comprobante'
      },
      paymentHistory: {
        title: 'Historial de Pagos',
        loading: 'Cargando pagos...',
        empty: 'No hay pagos registrados.',
        countLabel: '{count} pago(s) registrado(s)',
        colDate: 'Fecha',
        colConcept: 'Concepto',
        colAmount: 'Monto',
        colMethodStatus: 'Método / Estado',
        colReceipt: 'Comprobante',
        noReceipt: 'Sin comprobante',
        preview: 'Previsualizar',
        download: 'Descargar',
        copyReference: 'Copiar referencia'
      },
      paymentConfirmation: {
        processingTitle: 'Procesando tu pago',
        processingSubtitle: 'Estamos confirmando tu pago con Wompi…',
        approvedTitle: 'Pago confirmado',
        approvedSubtitle: 'Tu pago fue procesado correctamente.',
        declinedTitle: 'Pago rechazado',
        declinedSubtitle: 'El pago no se pudo procesar. Puedes intentarlo nuevamente.',
        errorTitle: 'Error en el pago',
        errorSubtitle: 'Ocurrió un error procesando el pago. Intenta nuevamente.',
        voidedTitle: 'Pago anulado',
        voidedSubtitle: 'El pago fue anulado.',
        pendingTitle: 'Pago pendiente',
        pendingSubtitle: 'El pago aún no se confirma. Recibirás el comprobante apenas se confirme.',
        referenceLabel: 'Referencia:',
        dateLabel: 'Fecha:',
        downloadReceipt: 'Descargar comprobante',
        generatingReceipt: 'Generando comprobante…',
        close: 'Cerrar',
        receiptNotReady: 'Comprobante aún no disponible',
        downloading: 'Descargando comprobante…',
        downloadError: 'Error al descargar el comprobante',
        pollError: 'No pudimos consultar el estado del pago. Reintenta en unos segundos.'
      },
      contactDialog: {
        addTitle: 'Agregar contacto',
        addOccupationTitle: 'Agregar ocupación',
        labelType: 'Tipo',
        labelName: 'Nombre',
        labelCell: 'Celular',
        labelEmail: 'Email',
        labelAddress: 'Dirección',
        labelOccupation: 'Ocupación',
        labelActivity: 'Actividad',
        labelCompany: 'Empresa',
        required: '*',
        allFieldsRequired: 'Todos los campos son obligatorios',
        addedContact: 'Contacto agregado',
        addedOccupation: 'Ocupación agregada',
        removedSuccess: 'Eliminado correctamente',
        selectContactError: 'Selecciona un contacto para eliminar',
        selectOccupationError: 'Selecciona una ocupación para eliminar',
        noContacts: 'No hay contactos registrados',
        noOccupations: 'No hay ocupaciones registradas',
        contactDefault: 'Contacto',
        occupationDefault: 'Ocupación'
      },
      contractTypeNames: {
        'PERSONA NATURAL': 'Persona Natural',
        'PERSONA JURIDICA': 'Persona Jurídica'
      },
      personalDocsList: {
        cedula_ciudadania: 'Copia de documento de identidad',
        cedula_ciudadania_desc: 'Copia del documento de identidad',
        certificado_afp: 'Certificado de la AFP (Opcional)',
        certificado_afp_desc: 'Certificado de afiliación a fondo de pensiones',
        rut: 'Copia del RUT (Opcional)',
        rut_desc: 'Registro Único Tributario',
        extracto_bancario: 'Extracto Bancario (últimos trimestres)',
        extracto_bancario_desc: 'Extractos bancarios recientes',
        certificado_ingresos: 'Certificado de Ingresos de los últimos 3 meses (o equivalente)',
        certificado_ingresos_desc: 'Certificado de ingresos de los últimos 3 meses (trimestre) o equivalente',
        carta_laboral: 'Carta Laboral (o equivalente)',
        carta_laboral_desc: 'Carta laboral o copia del contrato vigente',
        certificado_libertad_tradicion: 'Certificado de Libertad y Tradición (Opcional)',
        certificado_libertad_tradicion_desc: 'Certificado de propiedad de inmueble',
        certificado_existencia_representacion: 'Certificado de Existencia y Representación Legal',
        certificado_existencia_representacion_desc: 'Certificado de existencia y representación legal',
        cedula_representante: 'Copia de documento de identidad representante legal',
        cedula_representante_desc: 'Copia del documento de identidad del representante legal',
        certificado_libertad_tradicion_juridica: 'Certificado de Libertad y Tradición',
        certificado_libertad_tradicion_juridica_desc: 'Certificado de propiedad de inmueble',
        extracto_bancario_juridica: 'Extracto Bancario (últimos dos trimestres)',
        extracto_bancario_juridica_desc: 'Extractos bancarios recientes',
        referencia_comercial_1: 'Referencia Comercial 1',
        referencia_comercial_1_desc: 'Certificado de la primera referencia comercial',
        referencia_comercial_2: 'Referencia Comercial 2',
        referencia_comercial_2_desc: 'Certificado de la segunda referencia comercial',
        otros_documentos: 'Otros Documentos (Opcional)',
        otros_documentos_desc: 'Documentos adicionales que desee anexar'
      },
      externalLogin: {
        title: 'Portal de Cliente',
        subtitle: 'Ingresa con tu correo y número de documento',
        emailLabel: 'Correo electrónico',
        emailPlaceholder: 'tu@correo.com',
        passwordLabel: 'Número de documento (CC/NIT)',
        passwordPlaceholder: 'Ej: 1234567890',
        passwordHint: 'Ingresa el número de cédula o NIT con el que solicitaste el estudio',
        submit: 'Ingresar',
        submitting: 'Ingresando...',
        noStudy: '¿No tienes una solicitud?',
        requestHere: 'Solicita tu estudio aquí',
        requiredFields: 'Por favor completa todos los campos'
      },
      contractStatus: {
        INCOMPLETO: 'Incompleto',
        PENDIENTE: 'Pendiente',
        ACTIVO: 'Activo',
        VENCIDO: 'Vencido',
        TERMINADO: 'Terminado'
      },
      contractListStatus: {
        INCOMPLETO: 'Incompleto',
        PENDIENTE: 'Pendiente',
        ACTIVO: 'Activo',
        EN_PROGRESO: 'En Progreso',
        COMPLETADO: 'Completado',
        RECHAZADO: 'Rechazado'
      }
    },
    wizard: {
      stepLabels: {
        personalData: 'Datos personales',
        documents: 'Documentos',
        references: 'Referencias',
        payment: 'Pago'
      },
      stepperAria: 'Pasos de la solicitud',
      logoAria: 'Volver al inicio de BeSafe',
      footerHasAccount: '¿Ya tienes cuenta?',
      footerLogin: 'Inicia sesión',
      footerBackHome: '← Volver al inicio',
      step1: {
        intro: 'Empezamos con la información del solicitante. Crearemos un borrador del contrato y te asignaremos un asesor.',
        labelName: 'Nombre Completo',
        placeholderName: 'Juan Pérez García',
        labelType: 'Tipo de Persona',
        selectPlaceholder: 'Selecciona una opción',
        personaNatural: 'Persona Natural',
        personaJuridica: 'Persona Jurídica',
        labelExtranjero: 'El cliente es extranjero',
        labelTipoDoc: 'Tipo de Documento de Identidad',
        labelCcNit: 'Número de Documento de Identificación',
        placeholderCcNit: '123456789',
        hintCcNit: 'Número del documento. Admite letras, números y guiones (p. ej. pasaporte).',
        labelEmail: 'Correo Electrónico',
        placeholderEmail: 'tu@email.com',
        labelCellphone: 'Teléfono Celular',
        placeholderCellphone: '+57 300 1234567',
        propertyTitle: 'Información del Inmueble',
        labelAddress: 'Dirección del Inmueble',
        placeholderAddress: 'Calle 123 #45-67, Apto 301, Bogotá',
        labelRental: 'Expectativa Canon de Arrendamiento (COP | Pesos Colombianos)',
        placeholderRental: '1500000',
        hintRental: 'Valor en pesos colombianos (ej: 1500000)',
        consentPrefix: 'He leído y acepto la',
        consentPrivacyLink: 'Política de Privacidad',
        consentAnd: ' y la ',
        consentDataLink: 'Política de Tratamiento de Datos Personales',
        consentSuffix: ', y autorizo el envío de notificaciones sobre mi solicitud.',
        consentMissing: 'Debes aceptar la política de privacidad para continuar.',
        invalidField: 'Campo inválido',
        submit: 'Continuar al paso 2 →',
        creating: 'Creando solicitud...'
      },
      step2: {
        intro: 'Sube los documentos requeridos según tu tipo de persona. Los marcados con * son obligatorios.',
        contractTypeLabel: 'Tipo de persona:',
        back: '← Volver',
        next: 'Continuar a referencias →',
        uploaded: 'Subido · click en "Reemplazar" para cambiarlo',
        replace: 'Reemplazar',
        uploading: 'Subiendo…',
        dragPrefix: 'Arrastra',
        dragSuffix: 'o',
        dropSelect: 'selecciona un archivo',
        max: 'Máx',
        typeNotAllowed: 'Tipo no permitido. Permitidos: {types}',
        exceedsSize: 'Excede {max} MB',
        missingPrefix: 'Faltan: ',
        addMore: '+ Agregar otro archivo',
        removeFile: 'Quitar',
        reqBtn: 'Ver requisitos según tu actividad laboral',
        reqTitle: 'Requisitos según tu Actividad Laboral',
        reqIntro: 'Selecciona tu perfil para conocer los documentos que debes anexar:',
        reqClose: 'Cerrar',
        reqEmpleado: 'Empleado',
        reqIndependiente: 'Independiente',
        reqPensionado: 'Pensionado',
        reqEmpleadoDocs: 'Documento de identidad.|Carta laboral o copia del contrato vigente.|Últimos 2 extractos trimestrales de tu cuenta de ahorros.',
        reqIndependienteDocs: 'Documento de identidad.|Certificado de ingresos expedido por contador (adjuntando su Tarjeta Profesional).|Últimos 2 extractos trimestrales de tu cuenta de ahorros.|RUT o Cámara de Comercio actualizado.',
        reqPensionadoDocs: 'Documento de identidad.|Certificado de la AFP (Fondo de Pensiones).|Últimos 2 extractos trimestrales de tu cuenta de ahorros.'
      },
      step3: {
        intro: 'Agrega al menos <strong>dos referencias de contacto</strong> y una <strong>referencia ocupacional</strong>. Puedes registrar varios de cada tipo.',
        contactsTitle: 'Contactos de referencia',
        contactsHelp: 'Personas que puedan confirmar tu información (familiar, laboral, comercial, arrendador, emergencia). Debes registrar al menos dos.',
        contactsEmpty: 'Aún no has agregado contactos.',
        addContact: '+ Agregar contacto',
        occupationsTitle: 'Ocupaciones',
        occupationsHelp: 'Empresa o actividad económica del solicitante. Si tienes más de una, agrégalas todas.',
        occupationsEmpty: 'Aún no has agregado ocupaciones.',
        addOccupation: '+ Agregar ocupación',
        back: '← Volver',
        next: 'Continuar al pago →',
        labelName: 'Nombre',
        labelRelation: 'Relación',
        selectRelation: 'Selecciona…',
        labelPhone: 'Teléfono',
        labelEmail: 'Email',
        labelAddress: 'Dirección (Opcional)',
        labelOccupation: 'Ocupación de la referencia',
        labelActivity: 'Actividad / Cargo',
        labelCompany: 'Empresa',
        labelCompanyPhone: 'Teléfono empresa',
        labelCompanyEmail: 'Email empresa',
        labelCompanyAddress: 'Dirección empresa',
        cancel: 'Cancelar',
        saveContact: 'Guardar contacto',
        saveOccupation: 'Guardar ocupación',
        removeContactAria: 'Eliminar contacto',
        removeOccupationAria: 'Eliminar ocupación',
        remove: 'Quitar',
        missingContact: 'Al menos 2 contactos',
        missingOccupation: 'Al menos 1 ocupación',
        missingPrefix: 'Faltan: '
      },
      step4: {
        intro: 'Estás a un paso de solicitar tu estudio viabilidad, una vez confirmemos el pago de tu solicitud, un asesor te dará respuesta en 48 horas o menos.',
        loadingFees: 'Cargando tarifas del Estudio de Viabilidad…',
        loading: 'Cargando…',
        redirecting: 'Redirigiendo…',
        payWithWompi: 'Pagar {amount} con Wompi',
        applicant: 'Solicitante',
        type: 'Tipo',
        feasibilityStudy: 'Estudio de viabilidad',
        gatewayCommission: 'Comisión pasarela ({method})',
        ivaOnCommission: 'IVA sobre la comisión ({pct}%)',
        totalToPay: 'Total a pagar',
        paymentMethod: 'Método de pago',
        paymentMethodHelp: 'El total se actualiza según la tarifa de Wompi para cada método.',
        methods: {
          card: 'Tarjeta crédito/débito',
          bancolombia: 'Transferencia Bancolombia',
          puntos_colombia: 'Puntos Colombia'
        },
        studyCostMissing: 'El tenant no tiene configurado el costo del estudio para este tipo de persona.'
      },
      contactTypes: {
        'Contacto Familiar': 'Contacto Familiar',
        'Referencia Personal': 'Referencia Personal',
        'Referencia Laboral': 'Referencia Laboral',
        'Contacto de Emergencia': 'Contacto de Emergencia',
        'Referencia Comercial': 'Referencia Comercial',
        'Arrendador': 'Arrendador'
      }
    }
  },
  en: {
    nav: {
      services: 'Services',
      about: 'About us',
      faq: 'FAQ',
      contact: 'Contact',
      login: 'Login'
    },
    hero: {
      title: 'Renting Has Never Been So Easy',
      subtitle: 'We are the alternative that facilitates access to the real estate market through agile legal and financial analysis. Manage your entire process from our platform',
      uploadDocs: 'Start Study',
      paymentCenter: 'Payments',
      checkStatus: 'My Study'
    },
    about: {
      whoWeAre: {
        title: 'Our Purpose',
        content: 'We exist to break down barriers in the real estate market. We combine technology with the legal support of Apriori Legal SAS so that more people can access housing in a safe, agile and transparent way'
      },
      values: {
        title: 'Values: From "Words" to "Guarantees"',
        items: {
          0: {
            title: 'Total Legal Support',
            content: 'Every transaction is protected to safeguard the interests of all parties. We are not just a website, we are legal experts.'
          },
          1: {
            title: 'Financial Inclusion',
            content: 'We facilitate market access for new profiles, promoting equity and new housing opportunities.'
          },
          2: {
            title: 'Transparent Innovation',
            content: 'We use technology to make your processes clear, fast and without fine print.'
          }
        }
      }
    },
    services: {
      title: 'Our Services',
      items: {
        0: {
          title: 'Digital Credit Study',
          content: 'Agile analysis for your next lease. We evaluate your legal and financial profile in record time so you don\'t lose the property you want',
          button: 'Start Study'
        },
        1: {
          title: 'Payment and Collection Management',
          content: 'Your payments up to date, without complications. A centralized platform for paying your monthly fees with total transparency',
          button: 'Go to Payments'
        },
        2: {
          title: 'Be Safe Comprehensive Support',
          content: '360° protection for owners and real estate agencies. We cover legal, financial and operational analysis to guarantee secure leases',
          button: 'View Policies'
        }
      },
      howItWorks: {
        title: 'Renting and paying has never been so easy',
        subtitle: 'For New Tenants (Credit Study)',
        steps: {
          0: 'Online Application: Complete the credit study form from our platform in minutes.',
          1: 'Secure Upload: Upload your supporting documents with high security protocols.',
          2: 'Comprehensive Analysis: Our expert team performs the legal and financial evaluation.',
          3: 'Approval and Signature: Receive your response and formalize your lease with complete security.'
        },
        currentClients: {
          subtitle: 'For Current Clients (Payment of Fees)',
          steps: {
            0: 'Login: Access your private portal with your username and password.',
            1: 'Consultation: View your account status and pending fees transparently.',
            2: 'Agile Payment: Make payment through our integrated digital channels.',
            3: 'Support: Download your receipts and certificates instantly.'
          }
        },
        closing: 'And that\'s how EASY it is to lease your property with BESAFE'
      }
    },
    videoGallery: {
      title: 'Guide and Services Center',
      videos: {
        0: {
          title: 'Your Credit Study in 3 Steps',
          description: 'Explains how to request the study through the platform and the ease of uploading documents securely.'
        },
        1: {
          title: 'Payments and Collection Without Complications',
          description: 'Shows the payment portal, how to view pending fees and available electronic payment methods.'
        },
        2: {
          title: 'Be Safe Support',
          description: 'Explains the legal, financial and operational analysis performed by Apriori Legal SAS to provide security to all parties.'
        },
        3: {
          title: 'Inclusion: Opportunities for New Profiles',
          description: 'Describes how Be Safe allows access to the real estate market for profiles that do not have traditional guarantees.'
        }
      }
    },
    faq: {
      title: 'Frequently Asked Questions',
      subtitle: 'Find answers to the most common questions about our services',
      items: {
        0: {
          question: 'What is Be Safe?',
          answer: 'Be Safe is a solution created by Apriori Legal S.A.S. that facilitates property leasing when the tenant does not meet the requirements of a traditional insurance company. We act as support within the leasing process, generating security for owners, real estate agencies and tenants.'
        },
        1: {
          question: 'Is Be Safe a real estate agency?',
          answer: 'No. Be Safe does not manage properties. We work in partnership with real estate agencies and owners, providing support within the leasing process.'
        },
        2: {
          question: 'Who can access the service?',
          answer: 'The service is designed for individuals or companies that may have difficulties being approved by traditional insurance companies, for example: Foreigners without credit history in Colombia, Individuals without financial history in the country, SMEs without solid credit track record, Tenants with minor reports in risk bureaus.'
        },
        3: {
          question: 'How does the process work?',
          answer: 'The process is simple: The real estate agency or owner refers the interested party\'s information. Documentation is requested for analysis. A financial and legal feasibility study is conducted. If the profile is approved, agreements are formalized. Economic support is provided and the lease begins.'
        },
        4: {
          question: 'What is the feasibility study?',
          answer: 'It is an evaluation that allows verifying the economic capacity and background of the tenant to ensure they can meet the obligations of the lease contract.'
        },
        5: {
          question: 'How much does the study cost?',
          answer: 'Individual: $238,000 (VAT included). Legal entity: $357,000 (VAT included). The study is normally completed within approximately 48 business hours after receiving the documents and payment receipt.'
        },
        6: {
          question: 'Must a deposit or guarantee be provided?',
          answer: 'Yes. Once the study is approved, the tenant must provide economic support equivalent to up to three months\' rent, which serves as a guarantee against possible obligations arising from the contract.'
        },
        7: {
          question: 'Is the guarantee money returned?',
          answer: 'Yes. The guarantee is refunded at the end of the contract, once the real estate agency issues the clearance certificate. If there are pending amounts (such as utilities, repairs or rent), these may be deducted from the amount provided as guarantee.'
        },
        8: {
          question: 'What is the cost of the service?',
          answer: 'Be Safe charges a monthly technical service: 2.5% of the rental fee + VAT if the rent is equal to or less than $2,500,000. 2% of the rental fee + VAT if the rent is greater than $2,500,000.'
        },
        9: {
          question: 'What happens if the tenant is late with payment?',
          answer: 'In case of late payment of rent or service, a management procedure is activated that may include contact with the client and measures for property recovery according to signed agreements.'
        },
        10: {
          question: 'What are the advantages of using Be Safe?',
          answer: 'Expands leasing possibilities. Allows access to properties without depending on a traditional insurance company. Offers legal support and clear processes. Facilitates access for individuals or companies with non-traditional profiles in the real estate market.'
        }
      }
    },
    contact: {
      title: 'Contact Us',
      tagline: 'Comprehensive support for owners, real estate agencies and tenants, combining legal, financial and operational analysis to guarantee reliable processes',
      navigation: {
        title: 'Navigation',
        services: 'Services',
        about: 'About us',
        faq: 'FAQ',
        contact: 'Contact'
      },
      actions: {
        title: 'Quick Actions',
        requestStudy: 'Request credit study',
        payQuota: 'Pay my fee',
        uploadDocs: 'Upload documents'
      },
      info: {
        title: 'Contact Information',
        address: 'Calle 44 - 106 office 23, Centro Ejecutivo el Poblado, Medellín, Antioquia',
        whatsapp: '+57 312 4453260',
        email: 'apoyojuridico@apriorilegal.co',
        schedule: 'Monday to Friday: 8:00 AM - 5:00 PM'
      },
      allies: {
        title: 'Partners'
      },
      footer: {
        copyright: '© 2026 BeSafe – A priori Legal – All rights reserved',
        privacy: 'Privacy Policy',
        dataProtection: 'Data Protection Policy',
        developer: 'Developed by Jaibana IT'
      }
    },
    privacy: {
      title: 'Privacy Policy',
      responsible: {
        title: 'Data Controller',
        content: 'APRIORI LEGAL SAS. based in Medellín, Colombia, is responsible for the processing of personal data. According to our personal data processing policies, the mechanisms through which we use them are secure and confidential, as we have the appropriate technological means to ensure that they are stored in such a way that unwanted access by third parties is prevented, and in the same order we ensure their confidentiality.'
      },
      contact: {
        title: 'How to Contact Us',
        address: 'Office Address:',
        email: 'Email:',
        phone: 'Phone:'
      },
      purpose: {
        title: 'Processing Purposes',
        intro: 'Your personal data will be included in a database and will be used for the following purposes:',
        item1: 'Achieve efficient communication related to our products, services, offers, promotions, alliances, studies, contests, content, as well as those of our affiliated companies, and to facilitate general access to their information;',
        item2: 'Provide our services and products;',
        item3: 'Inform about new products or services that are related to those contracted or acquired;',
        item4: 'Fulfill obligations contracted with our clients, suppliers, and employees;',
        item5: 'Inform about changes to our products or services;',
        item6: 'Evaluate service quality, and',
        item7: 'Conduct internal studies on consumption habits.'
      },
      rights: {
        title: 'Your Rights',
        content: 'Data subjects are informed that they can consult the Internal Manual of Policies and Procedures of APRIORI LEGAL SAS, which contains our policies for the processing of collected information, as well as the consultation and complaint procedures that will allow them to exercise their rights to access, consultation, rectification, updating and deletion of data, by clicking on the following link:'
      },
      pdfLink: 'Download the Personal Data Processing Policy (PDF)'
    },
    dataProtection: {
      title: 'Data Protection Policy',
      intro: 'APRIORI LEGAL SAS is a company based in Medellín, Colombia, committed to complying with personal data regulations. APRIORI LEGAL SAS is committed to protecting privacy through the use, collection and processing of personal data of clients, suppliers and employees. APRIORI LEGAL SAS maintains strict confidentiality regarding personal data and guarantees the right of DATA SUBJECTS to know, update and rectify the information contained in our databases.',
      treatment: {
        title: 'Information Processing',
        content: 'Personal data incorporated by APRIORI LEGAL SAS into databases and files are processed exclusively for established purposes. Each personal data used by APRIORI LEGAL SAS has the respective authorization. Once the contractual, commercial or any other relationship between the Data Subject and the Company ends, the processing of personal data will cease, provided that the Data Subject does not have a legal or contractual duty to remain in the database.'
      },
      duties: {
        title: 'Duties of APRIORI LEGAL SAS as Data Controller',
        item1: 'Request and keep, under the conditions provided by law, a copy of the respective authorization granted by the Data Subject.',
        item2: 'Properly inform the Data Subject about the purpose of the collection and the rights they have by virtue of the authorization granted.',
        item3: 'Keep information under the necessary security conditions to prevent its alteration, loss, consultation, unauthorized or fraudulent use or access.',
        item4: 'Guarantee that the information provided to the Data Processor is truthful, complete, accurate, updated, verifiable and understandable.',
        item5: 'Process queries and claims filed under the terms indicated by law.'
      },
      rights: {
        title: 'Rights and Procedures',
        intro: 'APRIORI LEGAL SAS will guarantee DATA SUBJECTS access, rectification and deletion of personal data that has been processed and to request proof of the authorization granted for its processing.',
        access: {
          title: 'Right of Access',
          content: 'Every person will have the right to know if their personal data has been processed by APRIORI LEGAL SAS under the terms expressed in the Data Protection Law.'
        },
        rectification: {
          title: 'Rights of Rectification and Cancellation',
          content: 'DATA SUBJECTS have the right to verify with the data controller the accuracy and veracity of the data and request its rectification when they are inaccurate, incomplete or lead to error.'
        }
      },
      procedures: {
        title: 'Query and Claim Procedures',
        content: 'To exercise these rights, the data subject may do so through the means enabled by APRIORI LEGAL SAS:',
        phone: 'Phone:',
        email: 'Email:',
        address: 'Address:'
      },
      validity: {
        title: 'Policy Validity',
        content: 'Databases will have a validity equal to the period in which the purpose or purposes of processing in each database are maintained, or the validity period indicated in the Law, contracts, or respective capture formats.'
      },
      pdfLink: 'Download the Personal Data Processing Policy (PDF)'
    },
    login: {
      branding: {
        title: 'Security and trust in every lease',
        subtitle: 'Access your account to enter the management platform'
      },
      form: {
        title: 'Sign In',
        subtitle: 'Enter your credentials to access',
        email: 'Email Address',
        password: 'Password',
        remember: 'Remember me',
        forgot: 'Forgot your password?',
        submit: 'Sign In',
        noAccount: "Don't have an account?",
        register: 'Register here',
        backHome: '← Back to home'
      }
    },
    statusVerify: {
      title: 'Study Verification',
      subtitle: 'Enter your credentials to check your study status',
      userId: 'User Identifier',
      userIdHint: 'Example: USR-123456',
      password: 'Access Password',
      mfaCode: 'Verification Code',
      mfaHint: 'Enter the 6-digit code sent to your email',
      submit: 'Access',
      backHome: '← Back to home',
      error: 'Incorrect credentials. Please try again.',
      mfaModal: {
        title: 'Security Verification',
        description: 'We have sent a verification code to your registered email address and phone number.',
        codeLabel: 'Verification Code',
        codeHint: 'Enter the 6-digit code',
        verify: 'Verify Code',
        resend: 'Resend Code',
        error: 'Incorrect code. Please try again.',
        success: '✓ Code verified successfully'
      }
    },
    statusDashboard: {
      title: 'Your Study Status',
      subtitle: 'Track the progress of your lease application',
      logout: 'Sign Out',
      backHome: '← Back to home'
    },
    uploadDocs: {
      title: 'Document Management',
      subtitle: 'Select the option you need',
      backHome: '← Back to home',
      option1: {
        title: 'Request New Study',
        description: 'First time at BeSafe. Register and request your lease study.',
        button: 'Start Registration'
      },
      option2: {
        title: 'Upload Payment Receipt',
        description: 'Active user. Upload your monthly payment receipt.',
        button: 'Upload Receipt'
      },
      option3: {
        title: 'Upload Study Documents',
        description: 'Registered user. Upload required documents for your study.',
        button: 'Upload Documents'
      },
      newReg: {
        title: 'New Study Request',
        subtitle: 'Complete the form to start your registration process',
        firstName: 'First Name',
        lastName: 'Last Name',
        phone: 'Contact Phone',
        email: 'Email Address',
        message: 'Request Message',
        submit: 'Submit Request'
      },
      payment: {
        title: 'Upload Payment Receipt',
        subtitle: 'Verify your identity and upload your monthly payment receipt',
        userId: 'User ID',
        password: 'Password',
        verify: 'Verify Identity',
        month: 'Month',
        year: 'Year',
        file: 'Payment Receipt',
        dragDrop: 'Drag your file here or click to select',
        fileHint: 'PDF, JPG or PNG (max. 10MB)',
        submit: 'Upload Receipt'
      },
      study: {
        title: 'Upload Study Documents',
        subtitle: 'Verify your identity and upload required documents',
        userId: 'User ID',
        password: 'Password',
        verify: 'Verify Identity',
        selectType: 'Select document type',
        types: {
          id: '📄 Identification Document',
          financial: '💰 Financial Statement',
          support: '📋 Study Support Documents'
        },
        uploadedDocs: 'Uploaded Documents',
        noDocs: 'You haven\'t uploaded any documents yet',
        submit: 'Submit Documents'
      }
    },
    portal: {
      myContracts: 'My Contracts',
      welcome: 'Welcome',
      hero: 'Hi, {name}. Here you can see the status of all your contracts and procedures in one place.',
      activeContracts: 'Active contracts',
      emptyFilter: 'No contracts match this filter.',
      filters: {
        all: 'All',
        onTrack: 'Up to date',
        attention: 'Attention'
      },
      attention: {
        overdueOne: '{count} overdue contract',
        overdueMany: '{count} overdue contracts',
        soonOne: '{count} upcoming payment',
        soonMany: '{count} upcoming payments',
        unpaidOne: '{count} unpaid study',
        unpaidMany: '{count} unpaid studies'
      },
      detail: {
        tabSummary: 'Summary',
        subSummary: 'Contract, process and advisor',
        tabDocs: 'Documents',
        subDocs: 'Upload and review your file',
        tabPayments: 'Payments',
        subPayments: 'Account status and history',
        pendTitle: 'Need your attention',
        allGoodTitle: 'All up to date',
        payMoraTitle: 'Overdue payments',
        payOkTitle: 'Payment status',
        docsPendTitle: 'Pending documents',
        docsPendValue: '{count} to upload',
        docsDoneTitle: 'Documents',
        docsAllUploaded: 'All uploaded',
        docsHelp: 'Upload the required documents to move your study forward. They are renamed automatically.',
        requiredUploaded: 'required documents uploaded'
      },
      logout: 'Sign out',
      privacyPolicy: 'Personal Data Processing Policy',
      newStudy: 'Request new study',
      back: 'Back',
      financialInfo: 'Financial Information',
      assignedAdvisor: 'Assigned Advisor',
      processTimeline: 'Process Timeline',
      personalDocs: 'Personal Documents',
      personalInfo: 'Personal Information',
      personalInfoDesc: 'Contacts and occupations registered for the study',
      contacts: 'Contacts',
      occupations: 'Occupations',
      addNew: 'Add new',
      removeSelected: 'Remove selected',
      addContact: 'Add contact',
      addOccupation: 'Add occupation',
      cancel: 'Cancel',
      save: 'Save',
      update: 'Update',
      uploaded: 'Uploaded',
      noFiles: 'No files uploaded.',
      pendingPayments: 'Overdue payments',
      paymentsUpToDate: 'Up to date',
      paymentsUpToDateMsg: 'You have no pending payments. Keep it up!',
      payments: 'Payments',
      paymentsDesc: 'Pay your rent and security deposits with Wompi',
      registerPayment: 'Register Payment',
      payWithWompi: 'Pay with Wompi',
      paymentFullBalance: 'Full balance',
      paymentPartial: 'Partial payment',
      contactAdvisor: 'Contact Advisor',
      send: 'Send',
      noAdvisor: 'Advisor pending. Your advisor will be assigned soon.',
      historyTitle: 'Payment History',
      noPayments: 'No payments registered.',
      noContracts: 'No contracts found for your account.',
      requestStudy: 'Request Study',
      loading: 'Loading...',
      user: 'User',
      retry: 'Retry',
      errorTitle: 'Error',
      authRequired: 'You must sign in to access the portal',
      sessionExpired: 'Your session has expired. Please sign in again.',
      financial: {
        monthlyRent: 'Monthly rent',
        plusRent: 'Plus rent',
        backrest: 'Security deposit',
        commission: 'Commission',
        totalCommission: 'Total commission',
        duration: 'Term',
        months: 'months',
        property: 'Property',
        startDate: 'Start',
        endDate: 'End',
        empty: 'No financial information on record.'
      },
      defaults: {
        warningHeader: 'Overdue payments',
        warningBodyOne: 'You have <strong>{count} overdue payment</strong> for a total of <strong>{amount} COP</strong>.',
        warningBodyMany: 'You have <strong>{count} overdue payments</strong> for a total of <strong>{amount} COP</strong>.',
        warningFooter: 'Please bring your account current as soon as possible to avoid further action.'
      },
      advisor: {
        defaultName: 'Advisor',
        contactHelp: 'You can contact your advisor with any questions about your contract.',
        messagePlaceholder: 'Write your message to the advisor...',
        emptyMessage: 'Please write a message',
        sending: 'Sending...',
        sentSuccess: 'Message successfully sent to the advisor',
        loadingAdvisor: 'Loading advisor information...',
        emptyTitle: 'Advisor not yet assigned',
        emptySubtitle: 'Your advisor will be assigned soon'
      },
      timeline: {
        empty: 'No timeline information available.',
        title: 'Contract Progress',
        completedAt: 'Completed',
        startedAt: 'Started',
        itemNames: {
          'INICIO DEL PROCEDIMIENTO': 'Procedure Start',
          'DOCUMENTACIÓN': 'Documentation',
          'ESTUDIO': 'Study',
          'GESTIÓN CONTRACTUAL': 'Contractual Management',
          'FIRMA DE CONTRATO DE ARRENDAMIENTO': 'Lease Agreement Signing',
          'MONITOREO': 'Monitoring',
          'PAGO ESTUDIOS': 'Study Payment',
          'DOCUMENTOS NECESARIOS': 'Required Documents',
          'CLIENTE APROBADO': 'Client Approved',
          'PAGO DEL RESPALDO': 'Deposit Payment',
          'PAGO COMISION': 'Commission Payment',
          'FIRMA DE ACUERDO': 'Agreement Signing'
        },
        statuses: {
          'PENDIENTE': 'Pending',
          'EN PROCESO': 'In Progress',
          'COMPLETADO': 'Completed',
          'APROBADO': 'Approved',
          'RECHAZADO': 'Rejected',
          'NO APROBADO': 'Not Approved',
          'CANCELADO': 'Cancelled',
          'ACTIVO': 'Active',
          'FINALIZADO': 'Finalized'
        }
      },
      documents: {
        types: 'Types',
        max: 'Max',
        multipleFiles: 'Multiple files',
        filesLabel: 'file',
        filesLabelPlural: 'files',
        update: 'Update',
        empty: 'No files uploaded.',
        other: 'Other Documents',
        listTitle: 'Uploaded Documents',
        listEmpty: 'No documents uploaded.',
        listEmptyHint: 'Files you upload will appear here',
        loadingFiles: 'Loading documents...',
        filesCount: '{count} file(s)',
        colName: 'File',
        colSize: 'Size',
        colDate: 'Date',
        colStatus: 'Status',
        statusUploaded: 'Uploaded',
        typeNotAllowed: 'File type not allowed for {label}. Accepted: {types}',
        fileExceeds: 'File exceeds {max}MB for {label}',
        singleFileOnly: 'Only one file is allowed for {label}',
        uploadedSingle: '{count} file uploaded',
        uploadedMany: '{count} files uploaded',
        invalidType: 'File type not allowed: {ext}. Allowed: {types}',
        noExtension: 'no extension',
        tooLarge: 'File too large ({size}MB). Maximum: {max}MB',
        empty2: 'The file is empty',
        uploadSuccessAlt: 'File uploaded successfully',
        beforeUnload: 'Are you sure? The upload in progress will be cancelled.',
        dropzonePrompt: 'Drag files here or',
        selectFiles: 'Select files',
        allowedTypes: 'Allowed types',
        maxSize: 'Maximum size',
        confirmUpload: 'Confirm upload',
        cancel: 'Cancel',
        uploading: 'Uploading...',
        cancelUpload: 'Cancel upload',
        refresh: 'Refresh'
      },
      docsCategory: {
        studyTitle: 'Study Documents',
        studyDesc: 'Upload your identification, proof of income, references, etc. here.'
      },
      docsCompletedMsg: 'Your documentation is complete. Within the next <strong>48 hours</strong> we will conduct the study of your application.',
      contractsLoading: 'Loading contracts...',
      contractLabels: {
        advisor: 'Advisor:',
        advisorRole: 'Advisor',
        created: 'Created:',
        stepOf: 'of',
        rentCaption: 'Monthly rent',
        date: 'Date:',
        currentPhase: 'Current phase:',
        unassigned: 'Unassigned',
        viewDetails: 'View details →',
        property: 'Property:',
        monthlyRent: 'Monthly rent:',
        startDate: 'Start:',
        endDate: 'End:',
        paymentStatus: 'Payment status:',
        payStudy: 'Pay study',
        unpaidStudiesTitle: 'Unpaid studies',
        unpaidStudiesHelp: 'Requests that never completed the study payment. Continue the payment to activate them.',
        paymentAlDia: 'Up to date',
        paymentProximo: 'Payment due soon',
        paymentMora: 'Overdue',
        unknownAddress: 'Address unavailable',
        contractTitle: 'Contract',
        propertyInfoTitle: 'Property Information',
        address: 'Address:',
        period: 'Period:',
        type: 'Type:',
        rooms: 'Rooms:',
        documentsTitle: 'Documents',
        paymentsMgmt: 'Payment Management',
        backHome: '← Back to home'
      },
      paymentStatus: {
        upToDate: 'up to date',
        overdue: 'overdue'
      },
      paymentInfo: {
        title: 'Payment Information',
        statusLabel: 'Status: {status}',
        overdueCount: '({count} overdue payment(s))',
        monthlyService: 'Monthly service:',
        deposit: 'Security deposit:',
        nextPayment: 'Next payment:',
        lastPayment: 'Last payment:',
        notSpecified: 'Not specified',
        payWithWompiTitle: 'Pay with Wompi',
        payWithWompiNote: 'You will be redirected to the secure Wompi gateway'
      },
      paymentModal: {
        title: 'Register Payment',
        close: 'Close',
        intro: 'Pay now with Wompi (card, PSE, Nequi, Bancolombia). The receipt is generated automatically.',
        option: 'Payment option',
        full: 'Full balance',
        partial: 'Partial payment',
        partialHint: 'Choose the amount',
        defaultsSummary: 'Past-due balance: <strong>{defaults}</strong> + Monthly service: <strong>{service}</strong> = Total: <strong>{total}</strong>',
        monthSummary: 'Monthly service: <strong>{service}</strong>',
        respaldoSummary: 'Deposit to pay: <strong>{respaldo}</strong>',
        valueLabel: 'Amount ($)',
        conceptLabel: 'Concept',
        conceptPlaceholder: 'Select a concept',
        observationLabel: 'Note',
        observationPlaceholder: 'Additional notes (optional)',
        valueError: 'The amount must be greater than 0',
        conceptError: 'Invalid payment concept',
        observationLengthError: 'The note cannot exceed {max} characters',
        observationCharsError: 'The note contains disallowed characters',
        generating: 'Generating…',
        redirecting: 'Redirecting to Wompi…',
        previewError: 'Error previewing receipt',
        downloadError: 'Error downloading receipt',
        copySuccess: 'Reference copied',
        copyError: 'Could not copy the reference',
        manualSubmit: 'Send receipt',
        respaldoNote: 'The deposit is paid by bank transfer. Attach the receipt and an advisor will validate it.',
        dateLabel: 'Payment date',
        methodLabel: 'Payment method',
        methodPlaceholder: 'Select the method',
        methodError: 'Select the payment method',
        comprobanteLabel: 'Receipt (PDF or image)',
        comprobanteError: 'Attach the transfer receipt',
        uploading: 'Sending…',
        uploadSuccess: 'Receipt sent. An advisor will validate it.',
        uploadError: 'Could not send the receipt'
      },
      paymentHistory: {
        title: 'Payment History',
        loading: 'Loading payments...',
        empty: 'No payments registered.',
        countLabel: '{count} payment(s) registered',
        colDate: 'Date',
        colConcept: 'Concept',
        colAmount: 'Amount',
        colMethodStatus: 'Method / Status',
        colReceipt: 'Receipt',
        noReceipt: 'No receipt',
        preview: 'Preview',
        download: 'Download',
        copyReference: 'Copy reference'
      },
      paymentConfirmation: {
        processingTitle: 'Processing your payment',
        processingSubtitle: 'We are confirming your payment with Wompi…',
        approvedTitle: 'Payment confirmed',
        approvedSubtitle: 'Your payment was processed successfully.',
        declinedTitle: 'Payment declined',
        declinedSubtitle: 'The payment could not be processed. You can try again.',
        errorTitle: 'Payment error',
        errorSubtitle: 'An error occurred processing the payment. Please try again.',
        voidedTitle: 'Payment voided',
        voidedSubtitle: 'The payment was voided.',
        pendingTitle: 'Payment pending',
        pendingSubtitle: 'The payment has not yet been confirmed. You will receive the receipt as soon as it is confirmed.',
        referenceLabel: 'Reference:',
        dateLabel: 'Date:',
        downloadReceipt: 'Download receipt',
        generatingReceipt: 'Generating receipt…',
        close: 'Close',
        receiptNotReady: 'Receipt not yet available',
        downloading: 'Downloading receipt…',
        downloadError: 'Error downloading the receipt',
        pollError: 'We could not check the payment status. Please retry in a few seconds.'
      },
      contactDialog: {
        addTitle: 'Add contact',
        addOccupationTitle: 'Add occupation',
        labelType: 'Type',
        labelName: 'Name',
        labelCell: 'Mobile',
        labelEmail: 'Email',
        labelAddress: 'Address',
        labelOccupation: 'Occupation',
        labelActivity: 'Activity',
        labelCompany: 'Company',
        required: '*',
        allFieldsRequired: 'All fields are required',
        addedContact: 'Contact added',
        addedOccupation: 'Occupation added',
        removedSuccess: 'Removed successfully',
        selectContactError: 'Select a contact to remove',
        selectOccupationError: 'Select an occupation to remove',
        noContacts: 'No contacts registered',
        noOccupations: 'No occupations registered',
        contactDefault: 'Contact',
        occupationDefault: 'Occupation'
      },
      contractTypeNames: {
        'PERSONA NATURAL': 'Individual',
        'PERSONA JURIDICA': 'Legal entity'
      },
      personalDocsList: {
        cedula_ciudadania: 'Copy of Identity Document',
        cedula_ciudadania_desc: 'Copy of the identity document',
        certificado_afp: 'Pension Fund (AFP) Certificate (Optional)',
        certificado_afp_desc: 'Proof of enrollment in a pension fund',
        rut: 'Copy of Tax ID (RUT) (Optional)',
        rut_desc: 'Colombian Single Tax Registry (RUT)',
        extracto_bancario: 'Bank Statement (recent quarters)',
        extracto_bancario_desc: 'Recent bank statements',
        certificado_ingresos: 'Proof of Income for the last 3 months (or equivalent)',
        certificado_ingresos_desc: 'Proof of income for the last 3 months (quarter) or equivalent',
        carta_laboral: 'Employment Letter (or equivalent)',
        carta_laboral_desc: 'Employment letter or copy of the current contract',
        certificado_libertad_tradicion: 'Real Estate Title Certificate (Optional)',
        certificado_libertad_tradicion_desc: 'Proof of property ownership',
        certificado_existencia_representacion: 'Certificate of Existence and Legal Representation',
        certificado_existencia_representacion_desc: 'Company existence and legal representation certificate',
        cedula_representante: "Copy of Legal Representative's Identity Document",
        cedula_representante_desc: "Copy of the legal representative's identity document",
        certificado_libertad_tradicion_juridica: 'Real Estate Title Certificate',
        certificado_libertad_tradicion_juridica_desc: 'Proof of property ownership',
        extracto_bancario_juridica: 'Bank Statement (last two quarters)',
        extracto_bancario_juridica_desc: 'Recent bank statements',
        referencia_comercial_1: 'Commercial Reference 1',
        referencia_comercial_1_desc: 'Certificate from the first commercial reference',
        referencia_comercial_2: 'Commercial Reference 2',
        referencia_comercial_2_desc: 'Certificate from the second commercial reference',
        otros_documentos: 'Other Documents (Optional)',
        otros_documentos_desc: 'Additional documents you wish to attach'
      },
      externalLogin: {
        title: 'Client Portal',
        subtitle: 'Sign in with your email and ID number',
        emailLabel: 'Email address',
        emailPlaceholder: 'you@email.com',
        passwordLabel: 'ID number (CC/NIT)',
        passwordPlaceholder: 'e.g. 1234567890',
        passwordHint: 'Enter the national ID or tax ID number you used to request the study',
        submit: 'Sign in',
        submitting: 'Signing in...',
        noStudy: "Don't have a request yet?",
        requestHere: 'Request your study here',
        requiredFields: 'Please complete every field'
      },
      contractStatus: {
        INCOMPLETO: 'Incomplete',
        PENDIENTE: 'Pending',
        ACTIVO: 'Active',
        VENCIDO: 'Expired',
        TERMINADO: 'Terminated'
      },
      contractListStatus: {
        INCOMPLETO: 'Incomplete',
        PENDIENTE: 'Pending',
        ACTIVO: 'Active',
        EN_PROGRESO: 'In Progress',
        COMPLETADO: 'Completed',
        RECHAZADO: 'Rejected'
      }
    },
    wizard: {
      stepLabels: {
        personalData: 'Personal info',
        documents: 'Documents',
        references: 'References',
        payment: 'Payment'
      },
      stepperAria: 'Application steps',
      logoAria: 'Back to BeSafe home',
      footerHasAccount: 'Already have an account?',
      footerLogin: 'Sign in',
      footerBackHome: '← Back to home',
      step1: {
        intro: "Let's start with the applicant's information. We will create a contract draft and assign an advisor.",
        labelName: 'Full Name',
        placeholderName: 'John Smith',
        labelType: 'Applicant Type',
        selectPlaceholder: 'Select an option',
        personaNatural: 'Individual',
        personaJuridica: 'Legal entity',
        labelExtranjero: 'The client is a foreigner',
        labelTipoDoc: 'Identity Document Type',
        labelCcNit: 'Identification Number',
        placeholderCcNit: '123456789',
        hintCcNit: 'Document number. Allows letters, numbers and dashes (e.g. passport).',
        labelEmail: 'Email Address',
        placeholderEmail: 'you@email.com',
        labelCellphone: 'Mobile Phone',
        placeholderCellphone: '+57 300 1234567',
        propertyTitle: 'Property Information',
        labelAddress: 'Property Address',
        placeholderAddress: '123 Main St, Apt 301, Bogotá',
        labelRental: 'Expected Monthly Rent (COP | Colombian Pesos)',
        placeholderRental: '1500000',
        hintRental: 'Amount in Colombian pesos (e.g. 1500000)',
        consentPrefix: 'I have read and accept the',
        consentPrivacyLink: 'Privacy Policy',
        consentAnd: ' and the ',
        consentDataLink: 'Data Processing Policy',
        consentSuffix: ', and I authorize notifications about my request.',
        consentMissing: 'You must accept the privacy policy to continue.',
        invalidField: 'Invalid field',
        submit: 'Continue to step 2 →',
        creating: 'Creating request...'
      },
      step2: {
        intro: 'Upload the required documents according to your applicant type. Those marked with * are required.',
        contractTypeLabel: 'Applicant type:',
        back: '← Back',
        next: 'Continue to references →',
        uploaded: 'Uploaded · click "Replace" to change it',
        replace: 'Replace',
        uploading: 'Uploading…',
        dragPrefix: 'Drag',
        dragSuffix: 'or',
        dropSelect: 'select a file',
        max: 'Max',
        typeNotAllowed: 'File type not allowed. Allowed: {types}',
        exceedsSize: 'Exceeds {max} MB',
        missingPrefix: 'Missing: ',
        addMore: '+ Add another file',
        removeFile: 'Remove',
        reqBtn: 'View requirements by employment status',
        reqTitle: 'Requirements by Employment Status',
        reqIntro: 'Select your profile to see the documents you must attach:',
        reqClose: 'Close',
        reqEmpleado: 'Employee',
        reqIndependiente: 'Self-employed',
        reqPensionado: 'Retiree',
        reqEmpleadoDocs: 'Identity document.|Employment letter or copy of the current contract.|Last 2 quarterly statements of your savings account.',
        reqIndependienteDocs: 'Identity document.|Proof of income issued by an accountant (attaching their Professional Card).|Last 2 quarterly statements of your savings account.|Updated RUT or Chamber of Commerce certificate.',
        reqPensionadoDocs: 'Identity document.|Pension fund (AFP) certificate.|Last 2 quarterly statements of your savings account.'
      },
      step3: {
        intro: 'Add at least <strong>two contact references</strong> and one <strong>occupational reference</strong>. You can add several of each.',
        contactsTitle: 'Contact references',
        contactsHelp: 'People who can confirm your information (family, employer, commercial, landlord, emergency). You must add at least two.',
        contactsEmpty: 'You have not added any contacts yet.',
        addContact: '+ Add contact',
        occupationsTitle: 'Occupations',
        occupationsHelp: "Applicant's company or economic activity. If you have more than one, add them all.",
        occupationsEmpty: 'You have not added any occupations yet.',
        addOccupation: '+ Add occupation',
        back: '← Back',
        next: 'Continue to payment →',
        labelName: 'Name',
        labelRelation: 'Relationship',
        selectRelation: 'Select…',
        labelPhone: 'Phone',
        labelEmail: 'Email',
        labelAddress: 'Address (Optional)',
        labelOccupation: 'Reference occupation',
        labelActivity: 'Activity / Role',
        labelCompany: 'Company',
        labelCompanyPhone: 'Company phone',
        labelCompanyEmail: 'Company email',
        labelCompanyAddress: 'Company address',
        cancel: 'Cancel',
        saveContact: 'Save contact',
        saveOccupation: 'Save occupation',
        removeContactAria: 'Remove contact',
        removeOccupationAria: 'Remove occupation',
        remove: 'Remove',
        missingContact: 'At least 2 contacts',
        missingOccupation: 'At least 1 occupation',
        missingPrefix: 'Missing: '
      },
      step4: {
        intro: "You are one step away from requesting your viability study. Once we confirm the payment of your application, an advisor will reply within 48 hours or less.",
        loadingFees: 'Loading Viability Study fees…',
        loading: 'Loading…',
        redirecting: 'Redirecting…',
        payWithWompi: 'Pay {amount} with Wompi',
        applicant: 'Applicant',
        type: 'Type',
        feasibilityStudy: 'Viability study',
        gatewayCommission: 'Gateway commission ({method})',
        ivaOnCommission: 'VAT on commission ({pct}%)',
        totalToPay: 'Total to pay',
        paymentMethod: 'Payment method',
        paymentMethodHelp: 'The total is updated based on the Wompi fee for each method.',
        methods: {
          card: 'Credit/debit card',
          bancolombia: 'Bancolombia transfer',
          puntos_colombia: 'Puntos Colombia'
        },
        studyCostMissing: 'This tenant has not configured the study cost for this applicant type.'
      },
      contactTypes: {
        'Contacto Familiar': 'Family Contact',
        'Referencia Personal': 'Personal Reference',
        'Referencia Laboral': 'Employment Reference',
        'Contacto de Emergencia': 'Emergency Contact',
        'Referencia Comercial': 'Commercial Reference',
        'Arrendador': 'Landlord'
      }
    }
  }
};
